/**
 * Workspace-configurable reference lists for finance forms (service lines, banks).
 *
 * These replace the old hardcoded dropdown arrays so every option is managed from
 * Settings and persisted per tenant (mirrored to MongoDB via the KV sync). Reads
 * are synchronous from the local cache; the Settings screens write the lists.
 */
import { useEffect, useState } from 'react';
import { loadDevJson, saveDevJson } from './devStorage';
import type { CompanyBankAccount } from './paymentVia';
import { bankAccountLabel } from './paymentVia';

export const SERVICE_LINES_KEY = 'service_lines_v1';
export const BANKS_KEY = 'finance_banks_v1';
export const FINANCE_DEFAULTS_KEY = 'finance_defaults_v1';

export type CatalogRow = { id: string; name: string };

/** Workspace finance defaults that prefill new finance applications. */
export type FinanceDefaults = {
  /** Default interest: rupees per ₹100 principal (monthly basis in settings). */
  interestPercentage: string;
  /** Default out-payment (OP) interest value. */
  opInterest: string;
  /** Optional flat ₹ per overdue day (add-on per missed installment). */
  overduePenaltyPerDay: string;
  /** Overdue rate: rupees per ₹100 EMI per month (prorated daily), same model as EMI interest. */
  overduePenaltyPercent: string;
};

export function emptyFinanceDefaults(): FinanceDefaults {
  return { interestPercentage: '', opInterest: '', overduePenaltyPerDay: '', overduePenaltyPercent: '' };
}

const CATALOG_EVENT = 'finance-catalogs';

function emit() {
  try {
    window.dispatchEvent(new Event(CATALOG_EVENT));
  } catch {
    /* no window */
  }
}

/** Tolerant reader: supports the legacy `string[]` shape and the `{id,name}[]` shape. */
export function readCatalog(key: string): CatalogRow[] {
  const raw = loadDevJson<unknown>(key, []);
  if (!Array.isArray(raw)) return [];
  if (raw.length && typeof raw[0] === 'string') {
    return (raw as string[]).map((name) => ({ id: crypto.randomUUID(), name: String(name).trim() })).filter((r) => r.name);
  }
  return (raw as CatalogRow[]).filter((r) => r && typeof r.name === 'string' && r.name.trim());
}

export function writeCatalog(key: string, rows: CatalogRow[]): void {
  saveDevJson(key, rows);
  emit();
}

export function readServiceLineNames(): string[] {
  return readCatalog(SERVICE_LINES_KEY).map((r) => r.name);
}

export function readBankNames(): string[] {
  return readCompanyBanks().map((b) => bankAccountLabel(b));
}

/** Full company bank accounts (migrates legacy name-only rows). */
export function readCompanyBanks(): CompanyBankAccount[] {
  const raw = loadDevJson<unknown>(BANKS_KEY, []);
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const r = row as Record<string, unknown>;
      if (r.bankName && typeof r.bankName === 'string') {
        return {
          id: String(r.id || crypto.randomUUID()),
          bankName: String(r.bankName).trim(),
          accountName: String(r.accountName || '').trim(),
          accountNumber: String(r.accountNumber || '').trim(),
          ifsc: String(r.ifsc || '').trim(),
          branch: String(r.branch || '').trim(),
        } satisfies CompanyBankAccount;
      }
      if (r.name && typeof r.name === 'string') {
        return {
          id: String(r.id || crypto.randomUUID()),
          bankName: String(r.name).trim(),
          accountName: '',
          accountNumber: '',
          ifsc: '',
          branch: '',
        } satisfies CompanyBankAccount;
      }
      return null;
    })
    .filter((b): b is CompanyBankAccount => !!b && !!b.bankName);
}

export function writeCompanyBanks(rows: CompanyBankAccount[]): void {
  saveDevJson(BANKS_KEY, rows);
  emit();
}

function useCatalogNames(reader: () => string[]): string[] {
  const [names, setNames] = useState<string[]>(reader);
  useEffect(() => {
    const fn = () => setNames(reader());
    window.addEventListener(CATALOG_EVENT, fn);
    return () => window.removeEventListener(CATALOG_EVENT, fn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return names;
}

export function useServiceLineNames(): string[] {
  return useCatalogNames(readServiceLineNames);
}

export function useBankNames(): string[] {
  return useCatalogNames(readBankNames);
}

export function useCompanyBanks(): CompanyBankAccount[] {
  const [banks, setBanks] = useState<CompanyBankAccount[]>(readCompanyBanks);
  useEffect(() => {
    const fn = () => setBanks(readCompanyBanks());
    window.addEventListener(CATALOG_EVENT, fn);
    return () => window.removeEventListener(CATALOG_EVENT, fn);
  }, []);
  return banks;
}

/** Synchronous read of the workspace finance defaults. */
export function readFinanceDefaults(): FinanceDefaults {
  const raw = loadDevJson<Partial<FinanceDefaults> | null>(FINANCE_DEFAULTS_KEY, null);
  return {
    ...emptyFinanceDefaults(),
    ...(raw && typeof raw === 'object' ? raw : {}),
  };
}

export function writeFinanceDefaults(next: FinanceDefaults): void {
  saveDevJson(FINANCE_DEFAULTS_KEY, next);
  emit();
}

/** Live finance defaults bound to the catalog change event. */
export function useFinanceDefaults(): FinanceDefaults {
  const [defaults, setDefaults] = useState<FinanceDefaults>(readFinanceDefaults);
  useEffect(() => {
    const fn = () => setDefaults(readFinanceDefaults());
    window.addEventListener(CATALOG_EVENT, fn);
    return () => window.removeEventListener(CATALOG_EVENT, fn);
  }, []);
  return defaults;
}
